import useMarketDrivenShippingStatus from 'hooks/useMarketDrivenShippingStatus';
import usePermission from 'hooks/usePermission';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useAppSelector } from 'redux/hooks';
import { currentEcomSelector } from 'redux/slices/ecoms-slice';
import { EcomScope } from '../../../pb/scopes/v1/ecom_scope_pb';
import { SettingUrls } from '../../constants/SettingsUrls';

type NavItem = { path: string; name: string; rel?: string };

export const APPS_NAV_ITEM: NavItem = {
    path: SettingUrls.apps,
    name: 'SIDE_NAVIGATION.APPS',
};

export const NAV_ITEMS: NavItem[] = [
    {
        path: SettingUrls.dashboard,
        name: 'DASHBOARD',
        rel: 'home',
    },
    {
        path: SettingUrls.orders,
        name: 'SIDE_NAVIGATION.ORDERS',
    },
    {
        path: SettingUrls.shippingProviders,
        name: 'SIDE_NAVIGATION.SHIPPING_PROVIDERS',
    },
    {
        path: SettingUrls.shippingZones,
        name: 'SIDE_NAVIGATION.SHIPPING_ZONES_AND_RATES',
    },
    {
        path: SettingUrls.paymentMapping,
        name: 'SIDE_NAVIGATION.PAYMENT_MAPPING',
    },
    {
        path: SettingUrls.integrationMode,
        name: 'SIDE_NAVIGATION.INTEGRATION_MODE',
    },
    {
        path: SettingUrls.billing,
        name: 'SIDE_NAVIGATION.BILLING',
    },
    {
        path: SettingUrls.settingsProfile,
        name: 'SIDE_NAVIGATION.SETTINGS',
    },
];

const SCOPES_FALLBACK_TIMEOUT_MS = 3000;

export const SidebarNavigationEmbedded: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { hasPermission, isLoading } = usePermission();
    const [hasWaitedForScopes, setHasWaitedForScopes] = useState(false);
    const ecom = useAppSelector(currentEcomSelector);
    const { isActive: isMarketDrivenShippingActive } = useMarketDrivenShippingStatus();
    const [hasWaitedForNavData, setHasWaitedForNavData] = useState(false);

    useEffect(() => {
        const timeout = setTimeout(() => setHasWaitedForScopes(true), SCOPES_FALLBACK_TIMEOUT_MS);
        return () => clearTimeout(timeout);
    }, []);

    const handleNavigate = (event: React.MouseEvent<HTMLAnchorElement>, path: string) => {
        event.preventDefault();
        navigate({
            pathname: path,
        });
    };

    const hasAppsPermission = hasPermission(EcomScope.APPS_READ);

    const navItems = useMemo(() => {
        const items = isMarketDrivenShippingActive
            ? NAV_ITEMS.filter((item) => item.path !== SettingUrls.shippingZones)
            : NAV_ITEMS;
        return hasAppsPermission ? [...items, APPS_NAV_ITEM] : items;
    }, [hasAppsPermission, isMarketDrivenShippingActive]);

    // Render nothing until we know the scopes, so the nav appears once, complete, instead of
    // growing an extra item mid-load. Capped by the fallback timeout above.
    if (isLoading && !hasWaitedForScopes) {
        return null;
    }

    return (
        <>
            <s-app-nav>
                {navItems.map((item, index) => {
                    return (
                        <a
                            href={item.path}
                            key={index}
                            {...{ rel: item.rel }}
                            onClick={(e) => handleNavigate(e, item.path)}
                        >
                            {t(item.name as never)}
                        </a>
                    );
                })}
            </s-app-nav>
        </>
    );
};
